import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Plus,
  MoreVertical,
  Search,
  Pencil,
  Trash2,
  Loader2,
  CheckCircle,
  CircleAlert,
  Settings,
} from "lucide-react";
import { useBuyers } from "@/hooks/useBuyers";
import { useQueryClient } from "@tanstack/react-query";
import { countries } from "@/constants/countryCodes";
import { states } from "@/constants/states";
import { registrationTypes } from "@/constants/registrationTypes";
import { useToast } from "@/hooks/use-toast";
import { useCallout } from "@/hooks/use-callout";
import { buyerSchema, editBuyerSchema, newBuyerSchema } from "@/schema/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import LoadingIndicator from "@/components/LoadingIndicator";
import { AxiosError } from "axios";
import { BuyerResponse } from "@/types/buyer";
import { useEInvoices } from "@/hooks/useEInvoices";
import { useAuth } from "@/hooks/useAuth";
import TinSearch from "@/components/modal-tin";

const BuyerForm = ({
  initialData = null,
  onSubmit,
  isSubmitting,
}: {
  initialData: BuyerFormData | null;
  onSubmit: (data: BuyerFormData) => void;
  isSubmitting: boolean;
}) => {
  const isExisting = !!initialData;
  const schema = isExisting ? editBuyerSchema : newBuyerSchema;
  const form = useForm<BuyerFormData>({
    resolver: zodResolver(schema),
    defaultValues: initialData || {
      code: "",
      name: "",
      tin: "",
      registrationType: "",
      registrationNumber: "",
      sstRegistrationNumber: "",
      email: "",
      contactNumber: "",
      buyerAddress: {
        addressLine0: "",
        addressLine1: "",
        addressLine2: "",
        postalZone: "",
        cityName: "",
        state: "",
        country: "MYS",
      },
    },
  });

  const { getTaxpayerTin } = useEInvoices();
  const { user } = useAuth();
  const [openTinSearch, setOpenTinSearch] = useState<boolean>(false);
  const [isSearchTinLoading, setIsSearchTinLoading] = useState<boolean>(false);

  if (!user) {
    return <LoadingIndicator />;
  }

  const handleSearchTaxpayerTin = async () => {
    setIsSearchTinLoading(true);
    form.resetField("tin");

    try {
      const idType = form.getValues("registrationType");
      const idValue = form.getValues("registrationNumber");

      if (!idType || !idValue) {
        form.setError("tin", {
          type: "manual",
          message:
            "Please enter registration type and registration number to search for TIN",
        });
        return;
      }

      const taxpayerTin = await getTaxpayerTin({ idType, idValue, user });
      form.setValue("tin", taxpayerTin.tin);
      form.clearErrors("tin");
    } catch (error: any) {
      console.error("Error fetching taxpayer TIN:", error);
      form.resetField("tin");
      form.setError("tin", {
        type: "manual",
        message: error.message || "Failed to fetch TIN.",
      });
    } finally {
      setIsSearchTinLoading(false);
    }
  };

  return (
    <div className="space-y-6 pt-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            disabled={isExisting}
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base font-semibold">
                  Code {!isExisting && <span className="text-red-500">*</span>}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    className="h-10"
                    maxLength={300}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base font-semibold">Name</FormLabel>
                <FormControl>
                  <Input {...field} className="h-10" maxLength={300} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div>
            <FormField
              control={form.control}
              name="tin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold">TIN</FormLabel>
                  <div className="flex flex-row gap-1">
                    <FormControl className="flex-1">
                      <Input
                        {...field}
                        className="h-10"
                        maxLength={14}
                        onInput={() => {
                          form.clearErrors("tin");
                        }}
                      />
                    </FormControl>
                    <Button
                      variant="outline"
                      type="button"
                      onClick={() => setOpenTinSearch(true)}
                      className="group"
                    >
                      <Settings />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleSearchTaxpayerTin}
                      className=""
                    >
                      {isSearchTinLoading ? (
                        <Loader2 className="animate-spin text-gray-400" />
                      ) : (
                        <Search />
                      )}
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="registrationType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold">
                    Registration Type
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {registrationTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="registrationNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold">
                    Registration No.
                  </FormLabel>
                  <FormControl>
                    <Input {...field} className="h-10" maxLength={20} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sstRegistrationNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold">
                    SST Reg No.
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value || undefined}
                      className="h-10"
                      maxLength={17}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold">
                    Email
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      className="h-10"
                      maxLength={320}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold">
                    Contact Number
                  </FormLabel>
                  <FormControl>
                    <Input {...field} className="h-10" maxLength={15} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Address</h3>

            <FormField
              control={form.control}
              name="buyerAddress.addressLine0"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 1</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-10" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="buyerAddress.addressLine1"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 2</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      className="h-10"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="buyerAddress.addressLine2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 3</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      className="h-10"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="buyerAddress.postalZone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postal Code</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? undefined}
                        className="h-10"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="buyerAddress.cityName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input {...field} className="h-10" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="buyerAddress.state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="-- Select State --" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state.Code} value={state.Code}>
                          {state.State}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="buyerAddress.country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="-- Select Country --" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.Code} value={country.Code}>
                          {country.Country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 size={10} className="animate-spin text-primary" />
                  Saving
                </>
              ) : initialData ? (
                "Update Buyer"
              ) : (
                "Register Buyer"
              )}
            </Button>
            <SheetClose asChild>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </SheetClose>
          </div>
        </form>
      </Form>

      <TinSearch
        isOpen={openTinSearch}
        onClose={() => setOpenTinSearch(false)}
        onSelect={(value) => {
          form.setValue("tin", value);
          form.clearErrors("tin");
        }}
      />
    </div>
  );
};
interface BaseAddress {
  id?: string;
  addressLine0: string;
  addressLine1: string | null;
  addressLine2: string | null;
  postalZone: string | null;
  cityName: string;
  state: string;
  country: string;
}

interface BuyerFormData {
  code: string | null;
  id: string;
  name: string;
  tin: string;
  registrationType: string;
  registrationNumber: string;
  sstRegistrationNumber: string | null;
  email: string;
  contactNumber: string;
  updatedAt?: string;
  updatedBy?: string;
  buyerAddress: BaseAddress;
}

// Separate the search functionality into a custom hook same as the item list
const useBuyerSearch = (
  buyers: z.infer<typeof buyerSchema>[] | undefined | null,
) => {
  const [searchTerm, setSearchTerm] = React.useState("");

  const filteredBuyers = React.useMemo(() => {
    if (!Array.isArray(buyers)) {
      return [];
    }
    return buyers.filter((buyer) =>
      Object.values(buyer).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [buyers, searchTerm]);

  return { searchTerm, setSearchTerm, filteredBuyers };
};

export const BuyerList = () => {
  const { toast } = useToast();
  const { showNotification, hideNotification, CalloutNotification } =
    useCallout();
  const {
    buyers,
    isLoading,
    isLoadingError,
    error,
    createBuyerAsync,
    isCreating,
    updateBuyer,
    isUpdating,
    deleteBuyerAsync,
  } = useBuyers();
  const queryClient = useQueryClient();
  const [selectedBuyer, setSelectedBuyer] =
    React.useState<BuyerFormData | null>(null);
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);
  const { searchTerm, setSearchTerm, filteredBuyers } = useBuyerSearch(
    error || isLoadingError ? null : buyers?.data,
  );

  const cleanupBodyLock = () => {
    document.body.style.removeProperty("pointer-events");
    document.body.removeAttribute("data-scroll-locked");
  };

  const handleSubmit = async (data: BuyerFormData) => {
    hideNotification();
    let success = false;
    let toastTitle = "";
    let toastMessage = "";
    try {
      if (selectedBuyer) {
        const { id, code, ...formData } = data;

        const body = code ? { ...formData, code } : formData;

        await updateBuyer(
          { id: id, body },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ["buyers"] });
              success = true;
              toastTitle = "Update Successful";
              toastMessage = "The buyer has been successfully updated.";
            },
          },
        );
      } else {
        await createBuyerAsync(data as z.infer<typeof newBuyerSchema>, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["buyers"] });
            setIsSheetOpen(false);
            success = true;
            toastTitle = "Created Successful";
            toastMessage = "The buyer has been successfully created.";
          },
        });
      }
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const typedError: AxiosError<BuyerResponse> = error;
        toastMessage = typedError.response!.data.message;
      }
    } finally {
      if (success) {
        setIsSheetOpen(false);
        setSelectedBuyer(null);
        cleanupBodyLock();
        toast({
          title: toastTitle,
          description: toastMessage,
          duration: 5000,
          variant: success ? "default" : "destructive",
          icon: success ? (
            <CheckCircle
              size={30}
              className="animate-spin-once text-green-400"
            />
          ) : (
            <CircleAlert size={30} />
          ),
        });
      } else {
        showNotification({
          title: "Error",
          description: toastMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleEdit = (buyer: BuyerFormData) => {
    hideNotification();
    setSelectedBuyer(buyer);
    setIsSheetOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteBuyerAsync(id, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["buyers"] });
          toast({
            title: "Success",
            description: "Buyer has been deleted.",
            duration: 5000,
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-400"
              />
            ),
          });
        },
      });
    } catch (err) {
      console.error("Delete operation failed:", err);
    }
  };

  // const filteredBuyers = buyers.filter((buyer) =>
  //   Object.values(buyer).some((value) =>
  //     value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
  //   )
  // );
  if (isLoading) return <LoadingIndicator />;
  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search buyers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 w-[300px] shadow-sm"
            />
          </div>
        </div>
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <Button onClick={() => setSelectedBuyer(null)}>
              <Plus className="mr-2 h-4 w-4" />
              Register Buyer
            </Button>
          </SheetTrigger>
          <SheetContent
            className="overflow-y-auto"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              cleanupBodyLock();
            }}
          >
            <SheetHeader>
              <SheetTitle>
                {selectedBuyer ? "Edit Buyer" : "Register New Buyer"}
              </SheetTitle>
              <SheetDescription>
                {selectedBuyer
                  ? "Edit the buyer details below."
                  : "Fill in the buyer details below."}
              </SheetDescription>
            </SheetHeader>
            <BuyerForm
              initialData={selectedBuyer}
              onSubmit={handleSubmit}
              isSubmitting={isCreating || isUpdating}
            />
            <div className="space-y-6 pt-6">
              <CalloutNotification />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <Table>
        <TableHeader className="bg-slate-50 dark:bg-slate-800">
          <TableRow>
            <TableHead>Code</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Registration Type</TableHead>
            <TableHead>Registration No.</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead className="w-[50px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredBuyers.map((buyer) => (
            <TableRow key={buyer.id}>
              <TableCell className="font-medium">{buyer.code}</TableCell>
              <TableCell className="font-medium">{buyer.name}</TableCell>
              <TableCell>{buyer.registrationType}</TableCell>
              <TableCell>{buyer.registrationNumber}</TableCell>
              <TableCell>{buyer.email}</TableCell>
              <TableCell>{buyer.contactNumber}</TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEdit(buyer)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => handleDelete(buyer.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
